/* eslint-disable @typescript-eslint/return-await */
import { AxiosResponse } from "axios";

import API from "../api";
import { User } from "../models/user";
import { ApplicationType } from "../util/enums";

const ROUTE_NAME = "users";

const UserService = {
  async login(email: string, password: string): Promise<AxiosResponse<any>> {
    return await API.post(`/${ROUTE_NAME}/login`, { email, password, from: ApplicationType.WEB });
  },
  async logout(data: {
    userId: number;
    companyId: number;
    logoutFrom: string;
  }): Promise<AxiosResponse<any>> {
    return await API.post(`/${ROUTE_NAME}/logout`, data);
  },
  async count(options?: any): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/count`, { params: options });
  },
  async getById(id: number, options?: any): Promise<AxiosResponse<User> | null> {
    if (!id) return null;

    return await API.get(`/${ROUTE_NAME}/get/${id}`, { params: options });
  },
  async getCurrentUser(email?: string): Promise<AxiosResponse<User> | null> {
    if (!email) return null;

    return await API.get(`/${ROUTE_NAME}/current`);
  },

  async getAll(options?: any): Promise<AxiosResponse<User[]>> {
    return await API.get(`/${ROUTE_NAME}`, { params: options });
  },
  async create(user: User): Promise<AxiosResponse<User>> {
    return await API.post(`/${ROUTE_NAME}/register`, user);
  },
  async update(id?: number, user?: User): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${ROUTE_NAME}/${id}`, user);
  },
  async delete(id: number): Promise<AxiosResponse<User>> {
    return await API.delete(`/${ROUTE_NAME}/${id}`);
  },
  async forgotPassword(
    email: string
  ): Promise<AxiosResponse<{ success: boolean; message?: string } | boolean>> {
    return await API.post(`/${ROUTE_NAME}/forgot`, { email });
  },
  async confirmEmail(id: number): Promise<AxiosResponse<boolean>> {
    return await API.post(`/${ROUTE_NAME}/confirm/email/${id}`);
  },
  async recoverPassword(
    id: string,
    password: string,
    confirmPassword: string
  ): Promise<AxiosResponse<boolean>> {
    return await API.post(`/${ROUTE_NAME}/recover`, {
      id,
      password,
      confirmPassword,
    });
  },
  async initPassword(
    idOrEmail: string,
    password: string,
    confirmPassword: string
  ): Promise<AxiosResponse<boolean>> {
    const isEmail = idOrEmail && idOrEmail.includes("@");

    const payload = isEmail
      ? { email: idOrEmail, password, confirmPassword }
      : { id: idOrEmail, password, confirmPassword };

    return await API.post(`/${ROUTE_NAME}/init`, payload);
  },
  async resendConfirmationEmail(id: number): Promise<AxiosResponse<boolean>> {
    return await API.post(`/${ROUTE_NAME}/resend/email/${id}`);
  },
  async switchCompanyOld(userId: number, companyId: number): Promise<AxiosResponse<User>> {
    return await API.post(`/${ROUTE_NAME}/company/switch`, { userId, companyId });
  },
  async switchCompany(
    userId: number,
    companyId: number
  ): Promise<AxiosResponse<{ user: User; token: string }>> {
    return await API.post(`/${ROUTE_NAME}/company/new-switch`, { userId, companyId });
  },
  async createListOnboarding(users: User[]): Promise<AxiosResponse<User[]>> {
    return await API.post(`/${ROUTE_NAME}/create/list/onboarding`, users);
  },
  async checkPasswordComplexity(): Promise<AxiosResponse<any>> {
    return await API.get(`/users/company/security-config`);
  },
  async refreshToken(email: string): Promise<AxiosResponse<{ token: string }>> {
    return await API.get(`/${ROUTE_NAME}/refresh/token`, {
      params: { email, from: ApplicationType.WEB },
    });
  },
  async getUserPermissions(): Promise<AxiosResponse<any>> {
    return await API.get(`/permissions/get-by-current-user`);
  },
  async createSession(data: any): Promise<AxiosResponse<any>> {
    return await API.post(`/sessions/create`, data);
  },
};

export default UserService;
